import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { ALL_ROLES } from "../../api/types";
import type { User, UserRoleName } from "../../api/types";
import { S } from "../../styles";
import { LoadingState, ErrorState } from "../../components/QueryStates";
import { th, td, tableStyle } from "./adminStyles";

const EMPTY_CREATE = {
  keycloak_id: "",
  email: "",
  username: "",
  first_name: "",
  last_name: "",
  roles: ["buyer"] as UserRoleName[],
};

export function UsersPanel() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_CREATE });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [roleDraft, setRoleDraft] = useState<UserRoleName[]>([]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.listUsers(),
  });
  const users = data?.items ?? [];

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ["admin-users"] });

  const createMutation = useMutation({
    mutationFn: () =>
      api.createUser({
        keycloak_id: form.keycloak_id.trim(),
        email: form.email.trim(),
        username: form.username.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        roles: form.roles,
      }),
    onSuccess: (u) => {
      setMsg(`✅ Created user ${u.email}`);
      setForm({ ...EMPTY_CREATE, roles: ["buyer"] });
      setShowCreate(false);
      invalidate();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const rolesMutation = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: UserRoleName[] }) =>
      api.setUserRoles(id, roles),
    onSuccess: (u) => {
      setMsg(`✅ Updated roles for ${u.email}`);
      setEditingId(null);
      invalidate();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const activeMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.setUserActive(id, isActive),
    onSuccess: (u) => {
      setMsg(`✅ ${u.email} is now ${u.is_active ? "active" : "disabled"}`);
      invalidate();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      setMsg("✅ User removed");
      invalidate();
    },
    onError: (e: Error) => setMsg(`❌ ${e.message}`),
  });

  const startEdit = (u: User) => {
    setEditingId(u.id);
    setRoleDraft(
      u.roles.filter((r): r is UserRoleName =>
        (ALL_ROLES as string[]).includes(r),
      ),
    );
  };

  const toggleDraftRole = (role: UserRoleName) =>
    setRoleDraft((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );

  const toggleFormRole = (role: UserRoleName) =>
    setForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));

  const canCreate =
    form.keycloak_id.trim() &&
    form.email.trim() &&
    form.username.trim() &&
    form.roles.length > 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>👥 Users</h2>
        <button style={S.btnSm} onClick={() => void refetch()}>
          ↺ Refresh
        </button>
        <button style={S.btn} onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? "Cancel" : "+ New User"}
        </button>
      </div>
      <p style={{ ...S.muted, marginTop: 0, marginBottom: 16 }}>
        Manage tenant members and their roles. Users normally self-provision on
        first SSO login — use “New User” to pre-create an account.
      </p>

      {msg && (
        <div style={{ ...S.card, background: "#eef2ff", marginBottom: 12 }}>
          {msg}
        </div>
      )}

      {showCreate && (
        <div style={S.card}>
          <h3 style={S.h3}>Create user</h3>
          <div style={S.grid2}>
            <div>
              <label style={S.label}>Email</label>
              <input
                style={S.input}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="user@airline.com"
              />
            </div>
            <div>
              <label style={S.label}>Username</label>
              <input
                style={S.input}
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="jane.buyer"
              />
            </div>
            <div>
              <label style={S.label}>First name</label>
              <input
                style={S.input}
                value={form.first_name}
                onChange={(e) =>
                  setForm({ ...form, first_name: e.target.value })
                }
              />
            </div>
            <div>
              <label style={S.label}>Last name</label>
              <input
                style={S.input}
                value={form.last_name}
                onChange={(e) =>
                  setForm({ ...form, last_name: e.target.value })
                }
              />
            </div>
          </div>
          <label style={S.label}>Keycloak subject ID</label>
          <input
            style={S.input}
            value={form.keycloak_id}
            onChange={(e) => setForm({ ...form, keycloak_id: e.target.value })}
            placeholder="Must match the user's Keycloak `sub` claim"
          />
          <label style={S.label}>Roles</label>
          <RoleCheckboxes selected={form.roles} onToggle={toggleFormRole} />
          <div style={{ marginTop: 12 }}>
            <button
              style={{ ...S.btn, opacity: canCreate ? 1 : 0.5 }}
              disabled={!canCreate || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? "Creating…" : "Create user"}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <LoadingState label="Loading users…" />
      ) : error ? (
        <ErrorState error={error} />
      ) : users.length === 0 ? (
        <div style={S.card}>
          <p style={S.muted}>No users yet.</p>
        </div>
      ) : (
        <div style={{ ...S.card, overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>User</th>
                <th style={th}>Roles</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>
                      {u.first_name || u.last_name
                        ? `${u.first_name} ${u.last_name}`.trim()
                        : u.username}
                    </div>
                    <div style={S.muted}>{u.email}</div>
                  </td>
                  <td style={td}>
                    {editingId === u.id ? (
                      <RoleCheckboxes
                        selected={roleDraft}
                        onToggle={toggleDraftRole}
                      />
                    ) : (
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                      >
                        {u.roles.map((r) => (
                          <span key={r} style={S.roleBadge(r)}>
                            {r}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <span
                      style={S.statusBadge(u.is_active ? "Quoted" : "Expired")}
                    >
                      {u.is_active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td
                    style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}
                  >
                    {editingId === u.id ? (
                      <>
                        <button
                          style={S.btnSm}
                          disabled={
                            roleDraft.length === 0 || rolesMutation.isPending
                          }
                          onClick={() =>
                            rolesMutation.mutate({ id: u.id, roles: roleDraft })
                          }
                        >
                          Save
                        </button>{" "}
                        <button
                          style={S.btnGhost}
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button style={S.btnGhost} onClick={() => startEdit(u)}>
                          Edit roles
                        </button>{" "}
                        <button
                          style={S.btnGhost}
                          onClick={() =>
                            activeMutation.mutate({
                              id: u.id,
                              isActive: !u.is_active,
                            })
                          }
                        >
                          {u.is_active ? "Disable" : "Enable"}
                        </button>{" "}
                        <button
                          style={{ ...S.btnGhost, color: "#b91c1c" }}
                          onClick={() => {
                            if (window.confirm(`Remove user ${u.email}?`))
                              deleteMutation.mutate(u.id);
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RoleCheckboxes({
  selected,
  onToggle,
}: {
  selected: UserRoleName[];
  onToggle: (role: UserRoleName) => void;
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {ALL_ROLES.map((role) => (
        <label
          key={role}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={selected.includes(role)}
            onChange={() => onToggle(role)}
          />
          {role}
        </label>
      ))}
    </div>
  );
}
